import SearchIcon from '@mui/icons-material/Search';
import { Box, CircularProgress, Typography } from '@mui/material';
import { debounce, throttle } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchSearchedTranscriptions, fetchTranscriptions } from '../../api/endpoints';
import { Transcription } from '../../api/types';
import CustomTextField from '../../components/CustomTextField';
import useIsSmallMobile from '../../custom-hooks/useIsSmallMobile';
import RecordCard from './components/RecordCard';

const FilePage = () => {
  const isSmMobile = useIsSmallMobile();
  const [transcriptions, setTranscriptions] = useState<Transcription[]>([]);
  const [searchedTranscriptions, setSearchedTranscriptions] = useState<Transcription[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<Record<string, any> | undefined>(
    undefined
  );

  const observer = useRef<IntersectionObserver | null>(null);
  const isFetchingRef = useRef<boolean>(false);

  useEffect(() => {
    const handler = debounce((value: string) => {
      setDebouncedSearchTerm(value);
    }, 300);

    handler(searchTerm);

    return () => {
      handler.cancel();
    };
  }, [searchTerm]);

  const fetchData = useCallback(async () => {
    if (isFetchingRef.current || !hasMore) return;
    isFetchingRef.current = true;

    try {
      if (!lastEvaluatedKey) setLoading(true);
      else setLoadingMore(true);

      if (debouncedSearchTerm) {
        setTranscriptions([]);
        setLastEvaluatedKey(undefined);

        setLoading(true);
        const response = await fetchSearchedTranscriptions(debouncedSearchTerm);

        if (response && response.results && response.results.length > 0) {
          const newData: Transcription[] = response.results.map((item, index) => ({
            ...item,
            record_number: searchedTranscriptions.length + index + 1,
          }));

          setSearchedTranscriptions((prev) => [...prev, ...newData]);
        } else {
          setSearchedTranscriptions([]);
        }
        setLoading(false);
      } else {
        setSearchedTranscriptions([]);

        const response = await fetchTranscriptions(10, 'desc', lastEvaluatedKey);

        if (response && response.transcriptions && response.transcriptions.length > 0) {
          const newData: Transcription[] = response.transcriptions.map((item, index) => ({
            ...item,
            record_number: transcriptions.length + index + 1,
          }));

          setTranscriptions((prev) => [...prev, ...newData]);
          setLastEvaluatedKey(response.last_evaluated_key);
          setHasMore(response.has_more);
        } else {
          setHasMore(false);
        }
      }
    } catch (err) {
      toast.error('Failed to load transcriptions. Please try again later.');
      console.error('Error fetching transcriptions:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, [debouncedSearchTerm, lastEvaluatedKey, transcriptions.length, hasMore]);

  const lastTranscriptionElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading || loadingMore || !hasMore) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        throttle((entries) => {
          if (entries[0].isIntersecting) {
            fetchData();
          }
        }, 300)
      );

      if (node) {
        observer.current.observe(node);
      }
    },
    [loading, loadingMore, hasMore, fetchData]
  );

  useEffect(() => {
    const resetAndLoad = async () => {
      await fetchData();
    };
    resetAndLoad();
  }, [debouncedSearchTerm]);

  return (
    <Box
      sx={{
        p: 0,
        width: { xs: '100%', lg: '1200px' },
        maxWidth: 1200,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 600,
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: 'white',
          flexShrink: 0,
          width: { xs: '90%', sm: '80%' },
          mx: { xs: 'none', sm: 'auto' },
        }}
      >
        <CustomTextField
          placeholder="Search"
          type="text"
          prefix={<SearchIcon sx={{ fontSize: 20, color: '#333', mb: 1.8 }} />}
          value={searchTerm}
          onChange={(val) => setSearchTerm(val.target.value)}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" my={4} flexGrow={1}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            width: { xs: '90%', sm: '80%' },
            mx: { xs: 'none', sm: 'auto' },
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          }}
        >
          {debouncedSearchTerm.length > 0 ? (
            <>
              {searchedTranscriptions.map((transcription, index) => (
                <div
                  ref={
                    index === searchedTranscriptions.length - 1 ? lastTranscriptionElementRef : null
                  }
                  key={transcription.transcription_id}
                >
                  <RecordCard transcription={transcription} />
                </div>
              ))}

              {searchedTranscriptions.length === 0 && loading === false && (
                <Typography align="center" color="textSecondary" my={2}>
                  No records found for &quot;{debouncedSearchTerm}&quot;
                </Typography>
              )}
            </>
          ) : (
            <>
              {transcriptions.map((transcription, index) => (
                <div
                  ref={index === transcriptions.length - 1 ? lastTranscriptionElementRef : null}
                  key={transcription.transcription_id}
                >
                  <RecordCard transcription={transcription} />
                </div>
              ))}

              {loadingMore && (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress size={30} />
                </Box>
              )}

              {!hasMore && transcriptions.length > 0 && (
                <Typography align="center" color="textSecondary" my={2}>
                  No more records to load
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};

export default FilePage;
